/**
 * AVBP — Concerns checklist loader (M5 · 2026-06-20 · Anil).
 *
 * Loads + merges the concern palettes that ship in `public/concern-palettes/`:
 *   • `_base.json`            — universal concerns inherited by every type.
 *   • `{projectType}.json`    — per-type concerns (e.g. `residence.json`).
 *
 * Merge rule: `concern_id` is the dedupe key — a per-type concern OVERRIDES a
 * base concern with the same id. On a 404 of the per-type file we fall back to
 * the base list alone (empty per-type array). Extra fields present in the JSON
 * (synonyms, type_relevance, new_id_flag, …) are tolerated and carried through
 * untouched — we never drop them.
 *
 * `zone_correlated`: an OPTIONAL boolean. **Absent/undefined === correlated**
 * (true). Only the explicit `false` "amber" items are not zone-correlated; the
 * client never sees any visual difference (see ConcernsModule).
 *
 * Mirrors the public-asset fetch path used elsewhere in the portal
 * (e.g. the legacy `concernPalettes.ts` + `auoPalettes.ts`): `fetch('/concern-
 * palettes/…')` against Vite's served public root.
 */

export interface ConcernItem {
  concern_id: string;
  label: string;
  category: string;
  applies_per: string;
  severity_default: string;
  /** OPTIONAL — undefined/absent means correlated (true). Only explicit
   *  `false` marks an "amber" non-zone-correlated concern. */
  zone_correlated?: boolean;
  /** Tolerate any extra palette fields (synonyms, type_relevance, …). */
  [extra: string]: unknown;
}

/** The on-disk palette file shape (only the fields we read). */
interface PaletteFile {
  concerns?: unknown[];
}

/** Fetch + parse one palette JSON. Returns null on 404 / parse error. */
async function fetchPalette(url: string): Promise<PaletteFile | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const json = (await res.json()) as PaletteFile;
    if (!json || !Array.isArray(json.concerns)) return null;
    return json;
  } catch {
    return null;
  }
}

/** Coerce a raw palette entry into a ConcernItem, preserving extras. A row
 *  without a usable `concern_id` is dropped (returns null). */
function toConcernItem(raw: unknown): ConcernItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.concern_id !== 'string' || r.concern_id.length === 0) return null;
  return {
    ...r,
    concern_id: r.concern_id,
    label: typeof r.label === 'string' ? r.label : r.concern_id,
    category: typeof r.category === 'string' ? r.category : 'other',
    applies_per: typeof r.applies_per === 'string' ? r.applies_per : 'engagement',
    severity_default:
      typeof r.severity_default === 'string' ? r.severity_default : 'moderate',
    zone_correlated:
      typeof r.zone_correlated === 'boolean' ? r.zone_correlated : undefined,
  };
}

/**
 * Load the merged concern checklist for a project type. Base + per-type merged
 * by `concern_id` (per-type wins). Insertion order: base items first (in file
 * order), then any per-type items not already present — overrides keep the base
 * position. On a total miss returns an empty array (UI shows the loading/empty
 * state; the Continue button is still enabled — concerns are optional).
 */
export async function getMergedConcerns(
  projectType: string,
): Promise<ConcernItem[]> {
  const base = await fetchPalette('/concern-palettes/_base.json');
  const specific = projectType
    ? await fetchPalette(`/concern-palettes/${projectType}.json`)
    : null;

  const merged = new Map<string, ConcernItem>();
  for (const raw of base?.concerns ?? []) {
    const item = toConcernItem(raw);
    if (item) merged.set(item.concern_id, item);
  }
  for (const raw of specific?.concerns ?? []) {
    const item = toConcernItem(raw);
    if (item) merged.set(item.concern_id, item); // per-type overrides base
  }
  return Array.from(merged.values());
}
